// Shared role-form pieces — the scalar identity (name · description · inactivity
// lock) that BOTH the create wizard's Details step and the edit-details drawer use,
// so their validation and lock presets can't drift. Label strings stay in the
// consumers (i18n'd at wiring); this module is values-only (no user-facing copy).
//
// Bounds match the backend request schema (packages/schemas/roles/requests) so the
// form never accepts what the server rejects (CONVENTIONS §Schemas — never stricter
// than the backend): name ≤ 32 (roles.name varchar(32)), description ≤ 500 (TEXT cap).

import { z } from 'zod';

/** The role's scalar fields — spread into the create schema; the whole edit-details schema. */
export const roleDetailsShape = {
  name: z.string().trim().min(1).max(32),
  description: z.string().trim().max(500),
  inactivity_timeout_min: z.number().int().positive().nullable(),
};

export const roleDetailsSchema = z.object(roleDetailsShape);
export type RoleDetailsValues = z.infer<typeof roleDetailsSchema>;

/** Inactivity-lock presets (minutes); the consumer prepends a "no auto-lock" (null) option
 *  and labels 60 as "1 hour", 1 as "1 minute". */
export const LOCK_MINUTES = [1, 5, 10, 15, 30, 60] as const;

/** Clone payload passed via router state (the Clone act, RoleActions → the create wizard),
 *  which prefills the wizard from the source role so the admin renames + reviews
 *  before committing (no silent "(copy)" name collision). */
export interface RoleCloneState {
  name: string;
  description: string;
  inactivity_timeout_min: number | null;
  grants: string[];
  managed_roles: number[];
  responsibility_levels: string[];
}
